import { httpResource, HttpResourceRef, HttpResourceRequest } from '@angular/common/http';
import { computed, effect, inject, isSignal, linkedSignal, Signal, signal, WritableSignal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { cloneDeep, flatten, isEqual, uniq, uniqBy } from 'lodash';
import {
  ESqlOperators,
  FilterCardService,
  IDatePeriodFilterSave,
  IDateRangeFilterSave,
  IDateRangePickerDate,
  IPredefinedFilterSave,
  ISingleDateFilterSave,
  TDateRangeName,
  TFilterSave,
  DateUtilitiesService as WidgetDateUtilities,
} from 'scw-digital-boards-widgets';
import { IDropdownOption } from '../../shared/components/mat-dropdown/mat-dropdown.component';
import { DateUtilitiesService } from '../../utilities/date-utilities.service';
import { HttpUtilitiesService, IGetManyResponse } from '../../utilities/http-utilities.service';
import { TRecordWithSignalTypesExtracted } from '../interfaces/utility-types.interface';
import { EntityTranslatorService } from '../services/entity-translator-service';

export interface IDependencyValues {
  workOrderId: number[];
  siteIds: number[];
  lineId: number[];
  date: (IDateRangePickerDate<string> | string)[];
  activityId: number[];
  statusCategory: string[];
}

export interface ICommonParameters {
  search: WritableSignal<string>;
  initialIds: WritableSignal<number[] | null>;
}

export type THttpResourceWithTriggerParamsUpdater<UpdaterData, Fields extends keyof IDependencyValues> = (
  httpResourceRequest: HttpResourceRequest,
  utilities: {
    httpUtilities: HttpUtilitiesService;
  },
  parameters: {
    manualParameters: UpdaterData & TRecordWithSignalTypesExtracted<ICommonParameters>;
    dependencyParameters: Pick<IDependencyValues, Fields>;
  },
) => HttpResourceRequest;

export type TFormatter<Response> = (
  data: Response[],
  helpers: {
    translate: TranslateService;
    entityTranslatorService: EntityTranslatorService;
    filterCardService: FilterCardService;
  },
) => IDropdownOption<number | string>[];

export interface IHttpResourceWithTriggerConstructor<
  Response,
  UpdaterData extends Record<string, unknown> = {},
  Fields extends keyof IDependencyValues = keyof IDependencyValues,
> {
  httpResourceRequest: WritableSignal<HttpResourceRequest> | HttpResourceRequest;
  formatter: HttpResourceWithTrigger<Response, UpdaterData>['formatter'];
  paramsUpdater?: THttpResourceWithTriggerParamsUpdater<UpdaterData, Fields>;
  paramsUpdaterForInitialValues?: THttpResourceWithTriggerParamsUpdater<UpdaterData, Fields>;
  updateParametersDefault?: UpdaterData;
  dependencies: Fields[];
  triggerCondition?: () => boolean;
}

export class HttpResourceWithTrigger<
  Response,
  UpdaterData extends Record<string, unknown> = {},
  Fields extends keyof IDependencyValues = keyof IDependencyValues,
> {
  private readonly httpUtilities = inject(HttpUtilitiesService);
  private readonly translateService = inject(TranslateService);
  private readonly entityTranslatorService = inject(EntityTranslatorService);
  private readonly widgetDateUtilities = inject(WidgetDateUtilities);
  private readonly filterCardService = inject(FilterCardService);
  public readonly resource: HttpResourceRef<IGetManyResponse<Response> | undefined>;
  public readonly initialIdsResource: HttpResourceRef<IGetManyResponse<Response> | undefined> | undefined;
  public readonly httpResourceRequest: WritableSignal<HttpResourceRequest>;
  public readonly parameters: WritableSignal<UpdaterData>;
  public readonly commonParameters: ICommonParameters = {
    initialIds: signal(null, { equal: () => false }),
    search: signal(''),
  };
  public readonly trigger: WritableSignal<boolean>;
  public appliedFilters: Signal<TFilterSave[] | null> = signal<TFilterSave[] | null>(null);
  private readonly dependencyParameters: Signal<Pick<IDependencyValues, Fields>>;
  private readonly formatter: TFormatter<Response>;
  private readonly paramsUpdater: THttpResourceWithTriggerParamsUpdater<UpdaterData, Fields>;
  public formattedData!: Signal<IDropdownOption<number | string>[]>;
  private readonly formatterHelpers = {
    entityTranslatorService: this.entityTranslatorService,
    filterCardService: this.filterCardService,
    translate: this.translateService,
  };

  constructor({
    httpResourceRequest,
    formatter,
    paramsUpdater,
    paramsUpdaterForInitialValues,
    updateParametersDefault,
    dependencies,
    triggerCondition,
  }: IHttpResourceWithTriggerConstructor<Response, UpdaterData, Fields>) {
    this.httpResourceRequest = isSignal(httpResourceRequest) ? httpResourceRequest : signal(httpResourceRequest);
    this.formatter = formatter;
    this.paramsUpdater = paramsUpdater ?? ((): HttpResourceRequest => this.httpResourceRequest());
    this.trigger = triggerCondition ? linkedSignal<boolean>(triggerCondition) : signal(false);
    this.parameters = signal(updateParametersDefault ?? ({} as UpdaterData));
    this.dependencyParameters = this.getRequestFiltersWithIds(dependencies);

    this.resource = httpResource<IGetManyResponse<Response>>(() => {
      if (!this.trigger()) {
        return undefined;
      }

      return this.paramsUpdater(
        this.httpResourceRequest(),
        { httpUtilities: this.httpUtilities },
        {
          dependencyParameters: this.dependencyParameters(),
          manualParameters: {
            initialIds: this.commonParameters.initialIds(),
            search: this.commonParameters.search(),
            ...this.parameters(),
          },
        },
      );
    });

    this.initialIdsResource = httpResource<IGetManyResponse<Response>>(() => {
      if (!this.trigger() || !paramsUpdaterForInitialValues || !this.commonParameters.initialIds()?.length) {
        return undefined;
      }

      return paramsUpdaterForInitialValues(
        this.httpResourceRequest(),
        { httpUtilities: this.httpUtilities },
        {
          dependencyParameters: this.dependencyParameters(),
          manualParameters: {
            initialIds: this.commonParameters.initialIds(),
            search: this.commonParameters.search(),
            ...this.parameters(),
          },
        },
      );
    });

    this.assignFormattedData(Boolean(paramsUpdaterForInitialValues));
  }

  public updateParams(data: Partial<UpdaterData>): void {
    const updatedParameters = { ...this.parameters(), ...data };

    if (isEqual(updatedParameters, this.parameters())) {
      return;
    }

    this.trigger.set(true);
    this.parameters.set(cloneDeep(updatedParameters));
  }

  private getRequestFiltersWithIds(dependencies: Fields[]): Signal<Pick<IDependencyValues, Fields>> {
    return computed(
      () => {
        return dependencies.reduce(
          (reducePayload, dependency) => {
            const appliedFilters = this.appliedFilters();

            if (appliedFilters === null) {
              return reducePayload;
            }

            return {
              ...reducePayload,
              ...this.extractIdDependencies(appliedFilters, dependency),
              ...this.extractStringDependencies(appliedFilters, dependency),
              ...this.extractDateRangeDependencies(appliedFilters, dependency),
              ...this.extractDateNameDependencies(appliedFilters, dependency),
            };
          },
          {} as Pick<IDependencyValues, Fields>,
        );
      },
      { equal: isEqual },
    );
  }

  private extractIdDependencies(
    appliedFilters: TFilterSave[],
    dependency: Fields,
  ): Partial<Pick<IDependencyValues, Fields>> | {} {
    const ids = uniq(
      flatten(
        appliedFilters
          .filter(
            ({ field, operator, value }) =>
              field === dependency &&
              [ESqlOperators.$eq, ESqlOperators.$in].includes(operator) &&
              (typeof value === 'number' || (Array.isArray(value) && value.every((id) => typeof id === 'number'))),
          )
          .map(({ value }) => (Array.isArray(value) ? (value as number[]) : [value as number])),
      ),
    );

    return ids.length ? { [dependency]: ids } : {};
  }

  private extractStringDependencies(
    appliedFilters: TFilterSave[],
    dependency: Fields,
  ): Partial<Pick<IDependencyValues, Fields>> | {} {
    if (dependency !== 'statusCategory') {
      return {};
    }

    const values = uniq(
      flatten(
        appliedFilters
          .filter(
            ({ field, operator, value }) =>
              field === dependency &&
              [ESqlOperators.$eq, ESqlOperators.$in].includes(operator) &&
              (typeof value === 'string' || (Array.isArray(value) && value.every((item) => typeof item === 'string'))),
          )
          .map(({ value }) => (Array.isArray(value) ? (value as string[]) : [value as string])),
      ),
    );

    return values.length ? { [dependency]: values } : {};
  }

  private extractDateRangeDependencies(
    appliedFilters: TFilterSave[],
    dependency: Fields,
  ): Partial<Pick<IDependencyValues, Fields>> | {} {
    if (dependency !== 'date') {
      return {};
    }

    const dateRangeOperators: ESqlOperators[] = [
      ESqlOperators.$between,
      ESqlOperators.$lte,
      ESqlOperators.$gte,
      ESqlOperators.$lt,
      ESqlOperators.$gt,
    ] satisfies (IDateRangeFilterSave['operator'] | ISingleDateFilterSave['operator'])[];

    const values: IDateRangePickerDate<string>[] = (
      appliedFilters.filter(
        ({ field, operator }) => field === dependency && dateRangeOperators.includes(operator),
      ) as IDateRangeFilterSave[]
    ).map(({ value }) => ({
      endDate: DateUtilitiesService.convertUserDateStringToUTCString(value.endDate),
      startDate: DateUtilitiesService.convertUserDateStringToUTCString(value.startDate),
    }));

    return values.length ? { [dependency]: values } : {};
  }

  private extractDateNameDependencies(
    appliedFilters: TFilterSave[],
    dependency: Fields,
  ): Partial<Pick<IDependencyValues, Fields>> | {} {
    if (dependency !== 'date') {
      return {};
    }

    const predefinedDateOperators: ESqlOperators[] = [
      ESqlOperators.$period,
    ] satisfies IDatePeriodFilterSave['operator'][];

    const values = (
      appliedFilters.filter(
        ({ field, operator }) => field === dependency && predefinedDateOperators.includes(operator),
      ) as IPredefinedFilterSave<TDateRangeName>[]
    ).map((filter) => this.widgetDateUtilities.extractValuesFromDateFilter([filter])!);

    return values.length ? { [dependency]: values } : {};
  }

  private assignFormattedData(isServerSidePaginated: boolean): void {
    let initialLoad = isServerSidePaginated;

    if (isServerSidePaginated) {
      effect(() => {
        this.commonParameters.initialIds();
        initialLoad = true;
      });
    }

    this.formattedData = computed(() => {
      const data = this.resource.hasValue() ? this.resource.value().data : undefined;

      if (!initialLoad || !this.commonParameters.initialIds()?.length) {
        return data?.length ? uniqBy(this.formatter(data, this.formatterHelpers), 'id') : [];
      }

      const initialValues = this.initialIdsResource?.hasValue() ? this.initialIdsResource.value().data : undefined;

      if (initialValues === undefined || data === undefined) {
        return [];
      }

      initialLoad = false;

      return uniqBy(
        [...this.formatter(data, this.formatterHelpers), ...this.formatter(initialValues, this.formatterHelpers)],
        'id',
      );
    });
  }
}
